import { useEffect, useRef, useState, type FormEvent } from "react";
import { CONTACT_EMAIL } from "../config";

// Demo-request form — React port of the original site's inline script. Same fields, same custom
// dropdown (options, open/close on outside click and Escape), and the same no-backend submit
// behavior: builds a mailto: link to hello@drstack.ai with the field values.
const ORG_TYPES = [
  "Health system / hospital",
  "Employer",
  "Payer / insurer",
  "Digital health brand",
  "Pharmacy",
  "Other",
];

export function DemoForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [orgType, setOrgType] = useState(ORG_TYPES[0]);
  const [message, setMessage] = useState("");
  const [ddOpen, setDdOpen] = useState(false);
  const ddRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ddOpen) return;
    const onClick = (e: MouseEvent) => {
      if (ddRef.current && !ddRef.current.contains(e.target as Node)) setDdOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDdOpen(false);
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [ddOpen]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const subject = `Demo request — ${company}`;
    const body =
      `Name: ${name}\n` +
      `Email: ${email}\n` +
      `Company: ${company}\n` +
      `Organization type: ${orgType}\n\n` +
      `What we want to launch:\n${message}`;
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  return (
    <form className="demo-form" id="demoForm" onSubmit={onSubmit}>
      <div className="row">
        <div className="field">
          <label htmlFor="f-name">Full name</label>
          <input
            id="f-name"
            name="name"
            type="text"
            required
            placeholder="Jane Miller"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="f-email">Work email</label>
          <input
            id="f-email"
            name="email"
            type="email"
            required
            placeholder="jane@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      </div>
      <div className="row">
        <div className="field">
          <label htmlFor="f-company">Company</label>
          <input
            id="f-company"
            name="company"
            type="text"
            required
            placeholder="Acme Health"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </div>
        <div className="field">
          <label id="f-type-label">Organization type</label>
          <div className={`dd${ddOpen ? " open" : ""}`} ref={ddRef}>
            <button
              type="button"
              className="dd-toggle"
              id="f-type"
              aria-haspopup="listbox"
              aria-expanded={ddOpen}
              aria-labelledby="f-type-label"
              onClick={() => setDdOpen((o) => !o)}
            >
              <span className="dd-value">{orgType}</span>
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <div className="dd-menu" role="listbox" aria-labelledby="f-type-label">
              {ORG_TYPES.map((opt) => (
                <div
                  key={opt}
                  role="option"
                  aria-selected={opt === orgType}
                  className={`dd-opt${opt === orgType ? " selected" : ""}`}
                  onClick={() => {
                    setOrgType(opt);
                    setDdOpen(false);
                  }}
                >
                  <span>{opt}</span>
                  <svg
                    className="check"
                    viewBox="0 0 24 24"
                    fill="none"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="field">
        <label htmlFor="f-msg">What are you looking to launch?</label>
        <textarea
          id="f-msg"
          name="message"
          placeholder="e.g., Virtual urgent care for our 12,000 employees"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
      <button type="submit" className="btn btn-primary">
        Book a Demo
      </button>
      <p className="form-note">Submitting opens an email to {CONTACT_EMAIL} with your details.</p>
    </form>
  );
}
